import { useEffect, useState, useMemo } from 'react'
import { fetchAudit, type AuditEntry } from '../api/client'
import { Loader2, AlertCircle } from 'lucide-react'

export default function AuditPage() {
  const [entries, setEntries] = useState<AuditEntry[]>([])
  const [err, setErr] = useState<string | null>(null)
  const [pathFilter, setPathFilter] = useState('')
  const [actionFilter, setActionFilter] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const { entries: rows } = await fetchAudit({ limit: 500 })
        if (!cancelled) setEntries(rows)
      } catch {
        if (!cancelled) setErr('Could not load audit log.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const filtered = useMemo(() => {
    return entries.filter((e) => {
      if (pathFilter && !String(e.path ?? '').includes(pathFilter)) return false
      if (actionFilter) {
        const a = String(e.action ?? 'upload')
        if (a !== actionFilter) return false
      }
      return true
    })
  }, [entries, pathFilter, actionFilter])

  return (
    <div className="w-full max-w-[min(100%,72rem)] mx-auto px-4 lg:px-10 py-8 text-gray-200">
      <h1 className="text-2xl font-bold text-gray-100 mb-2">Audit log</h1>
      <p className="text-gray-500 text-sm mb-6">Recent events (newest first).</p>

      {err && (
        <div className="flex items-center gap-2 text-error-400 bg-red-950/30 border border-red-900/40 rounded-lg p-4 mb-4">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span>{err}</span>
        </div>
      )}

      <div className="flex flex-wrap gap-4 mb-4">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-gray-500">Filter by path substring</span>
          <input
            value={pathFilter}
            onChange={(e) => setPathFilter(e.target.value)}
            className="min-h-[44px] bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-gray-100 min-w-[200px] focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="e.g. Planning/"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-gray-500">Action</span>
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="min-h-[44px] bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All</option>
            <option value="upload">upload</option>
            <option value="edit">edit</option>
            <option value="delete">delete</option>
            <option value="login">login</option>
          </select>
        </label>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Loading audit log…
        </div>
      )}

      {!loading && (
        <div className="rounded-lg border border-gray-800 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-900/80 text-left text-gray-400">
            <tr>
              <th className="p-3 font-medium">Time</th>
              <th className="p-3 font-medium">Action</th>
              <th className="p-3 font-medium">Path</th>
              <th className="p-3 font-medium">User</th>
              <th className="p-3 font-medium">Source</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e, i) => (
              <tr key={`${e.at}-${e.path}-${i}`} className="border-t border-gray-800/80">
                <td className="p-3 text-gray-400 whitespace-nowrap">{e.at ?? '—'}</td>
                <td className="p-3">{e.action ?? 'upload'}</td>
                <td className="p-3 font-mono text-xs break-all">{e.path ?? '—'}</td>
                <td className="p-3">{e.username ?? '—'}</td>
                <td className="p-3 text-gray-500">{e.source ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && !err && (
          <p className="p-6 text-gray-500 text-center">No entries match.</p>
        )}
        </div>
      )}
    </div>
  )
}
